import { useQuery, useMutation, useQueryClient } from '@/lib/query';
import { toast } from 'sonner';
import { moduleDocAiService } from '../services/moduleDocAi.service';
import { CreateModuleDocAiPayload, ModuleDocAiQueryParams } from '../types';

export const useGetModuleDocAis = (params?: ModuleDocAiQueryParams) => {
  return useQuery({
    queryKey: ['moduleDocAis', params],
    queryFn: () => moduleDocAiService.getModuleDocAis(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetModuleDocAiById = (id: string | undefined) => {
  return useQuery({
    queryKey: ['moduleDocAis', id],
    queryFn: () => moduleDocAiService.getModuleDocAiById(id!),
    enabled: !!id,
  });
};

export const useCreateModuleDocAi = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateModuleDocAiPayload) =>
      moduleDocAiService.createModuleDocAi(payload),
    onSuccess: (response) => {
      if (response.status) {
        toast.success(response.message || 'Module Doc AI created successfully');
        queryClient.invalidateQueries({ queryKey: ['moduleDocAis'] });
      } else {
        toast.error(response.message || 'Failed to create Module Doc AI');
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Something went wrong');
    },
  });
};

export const useUpdateModuleDocAi = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateModuleDocAiPayload }) =>
      moduleDocAiService.updateModuleDocAi(id, payload),
    onSuccess: (response) => {
      if (response.status) {
        toast.success(response.message || 'Module Doc AI updated successfully');
        queryClient.invalidateQueries({ queryKey: ['moduleDocAis'] });
      } else {
        toast.error(response.message || 'Failed to update Module Doc AI');
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Something went wrong');
    },
  });
};

export const useDeleteModuleDocAi = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => moduleDocAiService.deleteModuleDocAi(id),
    onSuccess: (response) => {
      if (response.status) {
        toast.success(response.message || 'Module Doc AI deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['moduleDocAis'] });
      } else {
        toast.error(response.message || 'Failed to delete Module Doc AI');
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Something went wrong');
    },
  });
};

export const useUploadModuleDocAiFile = () => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      moduleDocAiService.uploadModuleDocAiFile(formData),
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to upload file');
    },
  });
};
